import type { EmailDeliveryFailureCode } from "@/lib/emailDelivery";
import type { Locale } from "@/content/site";

type DeliveryFailureCopy = Readonly<Record<EmailDeliveryFailureCode, Readonly<{
  title: string;
  body: string;
}>>>;

const contactDeliveryFailures = {
  en: {
    suppressed: {
      title: "Secure delivery is unavailable.",
      body: "The notification service cannot accept this inquiry. Your entries remain in the form.",
    },
    rate_limited: {
      title: "Please wait before trying again.",
      body: "The form has received too many recent attempts. Your entries remain in the form.",
    },
    invalid_origin: {
      title: "Submission was refused.",
      body: "This page was not recognized as an authorized Vert AI form. Reopen the form on vert-ai.com.",
    },
    validation_error: {
      title: "Some inquiry details were not accepted.",
      body: "Your entries remain in the form. Review field lengths and formats, then try again.",
    },
    queue_error: {
      title: "The inquiry was not queued.",
      body: "The delivery queue could not confirm receipt. Your entries remain in the form.",
    },
    server_error: {
      title: "Submission is temporarily unavailable.",
      body: "No inquiry was confirmed. Your entries remain in the form.",
    },
  } satisfies DeliveryFailureCopy,
  fr: {
    suppressed: {
      title: "La transmission sécurisée est indisponible.",
      body: "Le service de notification ne peut pas accepter cette demande. Vos renseignements demeurent dans le formulaire.",
    },
    rate_limited: {
      title: "Veuillez attendre avant de réessayer.",
      body: "Le formulaire a reçu trop de tentatives récentes. Vos renseignements demeurent dans le formulaire.",
    },
    invalid_origin: {
      title: "La transmission a été refusée.",
      body: "Cette page n’a pas été reconnue comme un formulaire Vert AI autorisé. Rouvrez le formulaire sur vert-ai.com.",
    },
    validation_error: {
      title: "Certains renseignements n’ont pas été acceptés.",
      body: "Vos renseignements demeurent dans le formulaire. Vérifiez la longueur et le format des champs, puis réessayez.",
    },
    queue_error: {
      title: "La demande n’a pas été mise en file.",
      body: "La file de transmission n’a pas pu confirmer la réception. Vos renseignements demeurent dans le formulaire.",
    },
    server_error: {
      title: "La transmission est temporairement indisponible.",
      body: "Aucune demande n’a été confirmée. Vos renseignements demeurent dans le formulaire.",
    },
  } satisfies DeliveryFailureCopy,
} as const;

const scorecardDeliveryFailures = {
  en: {
    suppressed: {
      title: "Secure delivery is unavailable.",
      body: "The notification service cannot accept these results. Your answers remain visible.",
    },
    rate_limited: {
      title: "Please wait before trying again.",
      body: "The form has received too many recent attempts. Your answers remain visible.",
    },
    invalid_origin: {
      title: "Submission was refused.",
      body: "This page was not recognized as an authorized Vert AI form. Reopen the scorecard on vert-ai.com.",
    },
    validation_error: {
      title: "Some details were not accepted.",
      body: "Your answers remain visible. Review the email and context length, then try again.",
    },
    queue_error: {
      title: "The results were not queued.",
      body: "The delivery queue could not confirm receipt. Your answers remain visible.",
    },
    server_error: {
      title: "Submission is temporarily unavailable.",
      body: "No submission was confirmed. Your answers remain visible.",
    },
  } satisfies DeliveryFailureCopy,
  fr: {
    suppressed: {
      title: "La transmission sécurisée est indisponible.",
      body: "Le service de notification ne peut pas accepter ces résultats. Vos réponses demeurent affichées.",
    },
    rate_limited: {
      title: "Veuillez attendre avant de réessayer.",
      body: "Le formulaire a reçu trop de tentatives récentes. Vos réponses demeurent affichées.",
    },
    invalid_origin: {
      title: "La transmission a été refusée.",
      body: "Cette page n’a pas été reconnue comme un formulaire Vert AI autorisé. Rouvrez la grille sur vert-ai.com.",
    },
    validation_error: {
      title: "Certains renseignements n’ont pas été acceptés.",
      body: "Vos réponses demeurent affichées. Vérifiez le courriel et la longueur du contexte, puis réessayez.",
    },
    queue_error: {
      title: "Les résultats n’ont pas été mis en file.",
      body: "La file de transmission n’a pas pu confirmer la réception. Vos réponses demeurent affichées.",
    },
    server_error: {
      title: "La transmission est temporairement indisponible.",
      body: "Aucun envoi n’a été confirmé. Vos réponses demeurent affichées.",
    },
  } satisfies DeliveryFailureCopy,
} as const;

export const utilityRouteCopy = {
  common: {
    en: {
      home: "Home",
      services: "Services",
      industries: "Industries",
      resources: "Resources",
      contact: "Contact",
      privacyLink: "Review the privacy information.",
      nextStep: "Next step",
      primaryCta: "Discuss a production AI opportunity",
    },
    fr: {
      home: "Accueil",
      services: "Mandats",
      industries: "Secteurs",
      resources: "Ressources",
      contact: "Contact",
      privacyLink: "Voir les renseignements sur la confidentialité.",
      nextStep: "Prochaine étape",
      primaryCta: "Discuter d’un projet d’IA en production",
    },
  },
  contact: {
    en: {
      required: "Required",
      errorSummaryTitle: "Correct these fields before sending.",
      requiredFields: {
        name: "Add your name before sending.",
        email: "Add your work email before sending.",
        company: "Add your company name before sending.",
        message: "Describe the workflow or decision before sending.",
      },
      breadcrumbHome: "Home",
      unavailable: "The form cannot be sent right now. Email the same context or use the booking link instead.",
      subject: "Vert AI — costly workflow or recurring decision",
      validationToastTitle: "Required information is missing.",
      validationToastBody: "Please correct the highlighted fields before sending.",
      reviewRouter: {
        eyebrow: "Decision-review path",
        title: "Already facing a funding, launch, or scale decision for one AI initiative?",
        body: "Use the AI Verification Review application. The general contact form remains available for implementation, data-platform, and decision-system inquiries.",
        cta: "Use the Review Application",
      },
      scorecardLink: "Run the AI readiness scorecard before the call",
      retryOrEmail: "Try again or email the same workflow context directly.",
      honeypot: {
        title: "The inquiry was not submitted.",
        body: "Review the form and try again. No inquiry was submitted.",
      },
      identityLegend: "Identity / 01",
      needLegend: "Need / 02",
      deliveryAvailable: "Sends to Vert AI contact email",
      privacyNotice: "By submitting, you ask Vert AI to use this information to review the inquiry and respond.",
      emailLabels: {
        name: "Name",
        email: "Work email",
        company: "Company",
        message: "Costly workflow or recurring decision",
      },
      retryLater: " Try again later.",
      retryAfter: (minutes: number) => ` Try again in about ${minutes} minute${minutes > 1 ? "s" : ""}.`,
      deliveryFailures: contactDeliveryFailures.en,
    },
    fr: {
      required: "Requis",
      errorSummaryTitle: "Corrigez ces champs avant l’envoi.",
      requiredFields: {
        name: "Ajoutez votre nom avant l’envoi.",
        email: "Ajoutez votre courriel professionnel avant l’envoi.",
        company: "Ajoutez le nom de votre entreprise avant l’envoi.",
        message: "Décrivez le processus ou la décision avant l’envoi.",
      },
      breadcrumbHome: "Accueil",
      unavailable: "Le formulaire ne peut pas être transmis pour l’instant. Envoyez le même contexte par courriel ou utilisez le lien de réservation.",
      subject: "Vert AI — processus coûteux ou décision récurrente",
      validationToastTitle: "Renseignements requis manquants.",
      validationToastBody: "Veuillez corriger les champs indiqués avant l’envoi.",
      reviewRouter: {
        eyebrow: "Parcours décisionnel",
        title: "Vous devez déjà prendre une décision de financement, de lancement ou d’expansion pour une initiative d’IA?",
        body: "Utilisez la demande d’examen de vérification de l’IA. Le formulaire de contact général demeure disponible pour les mandats de mise en œuvre, les plateformes de données et les systèmes décisionnels.",
        cta: "Utiliser la demande d’examen",
      },
      scorecardLink: "Remplir la grille de préparation à l’IA avant la discussion",
      retryOrEmail: "Réessayez ou envoyez le même contexte directement par courriel.",
      honeypot: {
        title: "La demande n’a pas été transmise.",
        body: "Vérifiez le formulaire et réessayez. Aucune demande n’a été transmise.",
      },
      identityLegend: "Identité / 01",
      needLegend: "Besoin / 02",
      deliveryAvailable: "Envoi au courriel de contact de Vert AI",
      privacyNotice: "En envoyant ce formulaire, vous demandez à Vert AI d’utiliser ces renseignements pour examiner votre demande et vous répondre.",
      emailLabels: {
        name: "Nom",
        email: "Courriel professionnel",
        company: "Entreprise",
        message: "Processus coûteux ou décision récurrente",
      },
      retryLater: " Réessayez plus tard.",
      retryAfter: (minutes: number) => ` Réessayez dans environ ${minutes} minute${minutes > 1 ? "s" : ""}.`,
      deliveryFailures: contactDeliveryFailures.fr,
    },
  },
  readiness: {
    en: {
      unavailable: "The result-sharing form cannot be sent right now. Email the scorecard context instead.",
      breadcrumbHome: "Home",
      subject: "AI readiness scorecard",
      invalidEmailTitle: "Enter a valid work email.",
      senderName: "AI Readiness Scorecard",
      notProvided: "Not provided",
      start: "Start the scorecard",
      reviewLive: "Review a Live AI Initiative",
      modelLabel: "Model / 10",
      stats: [
        { label: "Dimensions", value: "10" },
        { label: "Max score", value: "30" },
        { label: "Email", value: "Optional" },
      ],
      foundation: "Readiness foundation",
      decision: {
        eyebrow: "Material decision path",
        title: "The scorecard identifies questions. The AI Verification Review inspects the evidence.",
        body: "Use this path for a live or decision-ready AI initiative when leadership must fund, launch, renew a vendor, pause, or scale. A high directional score is not proof of production readiness.",
      },
      apply: "Apply for the Review",
      recommendedService: "Review recommended service",
      finalTitle: "When the decision is material, verify it against the real evidence.",
      finalBody: "The AI Verification Review turns one initiative’s evidence, controls, ownership, and economics into a Fund, Fix, Pause, Scale, Stop, or Reframe recommendation.",
      requiredToShare: "Required to share",
      disabledHelp: "Answer all 10 dimensions before sharing scorecard context.",
      retryOrEmail: "Try again or email the same scorecard details.",
      honeypot: {
        title: "The results were not submitted.",
        body: "Review the form and try again. Nothing was submitted.",
      },
      shareNotice: "This sends your answers and context to Vert AI; it does not send you an automated report.",
      emailLabels: {
        contact: "Follow-up email",
        context: "Context",
        score: "Score",
        band: "Readiness band",
        service: "Recommended service",
        answers: "Answers",
        empty: "Not provided",
      },
      retryLater: " Try again later.",
      retryAfter: (minutes: number) => ` Try again in about ${minutes} minute${minutes > 1 ? "s" : ""}.`,
      deliveryFailures: scorecardDeliveryFailures.en,
    },
    fr: {
      unavailable: "Le formulaire de partage ne peut pas être transmis pour l’instant. Envoyez plutôt le contexte de la grille par courriel.",
      breadcrumbHome: "Accueil",
      subject: "Grille de préparation à l’IA",
      invalidEmailTitle: "Entrez un courriel professionnel valide.",
      senderName: "Grille de préparation à l’IA",
      notProvided: "Non fourni",
      start: "Commencer la grille d’évaluation",
      reviewLive: "Faire examiner une initiative en exploitation",
      modelLabel: "Grille / 10",
      stats: [
        { label: "Dimensions", value: "10" },
        { label: "Maximum", value: "30" },
        { label: "Courriel", value: "Optionnel" },
      ],
      foundation: "Fondation",
      decision: {
        eyebrow: "Décision importante",
        title: "La grille soulève les questions. L’examen de vérification de l’IA inspecte les preuves.",
        body: "Ce parcours s’adresse aux initiatives d’IA en exploitation ou prêtes à faire l’objet d’une décision lorsque la direction doit financer, lancer, renouveler un fournisseur, mettre en pause ou élargir le système. Le résultat indicatif ne constitue pas une preuve de préparation à la production.",
      },
      apply: "Présenter une demande",
      recommendedService: "Voir le mandat recommandé",
      finalTitle: "Lorsque la décision est importante, vérifiez-la avec les preuves réelles.",
      finalBody: "L’examen de vérification de l’IA transforme les preuves, les contrôles, les responsabilités et la viabilité économique d’une initiative en recommandation de direction : financer, corriger, mettre en pause, élargir, arrêter ou recadrer.",
      requiredToShare: "Requis pour transmettre",
      disabledHelp: "Répondez aux 10 dimensions avant de transmettre les résultats.",
      retryOrEmail: "Réessayez ou envoyez les mêmes détails par courriel.",
      honeypot: {
        title: "Les résultats n’ont pas été transmis.",
        body: "Vérifiez le formulaire et réessayez. Aucun envoi n’a été effectué.",
      },
      shareNotice: "Cette action transmet vos réponses et votre contexte à Vert AI; elle ne vous envoie pas un rapport automatisé.",
      emailLabels: {
        contact: "Courriel de suivi",
        context: "Contexte",
        score: "Résultat indicatif",
        band: "Niveau de préparation",
        service: "Type de mandat recommandé",
        answers: "Réponses",
        empty: "Non fourni",
      },
      retryLater: " Réessayez plus tard.",
      retryAfter: (minutes: number) => ` Réessayez dans environ ${minutes} minute${minutes > 1 ? "s" : ""}.`,
      deliveryFailures: scorecardDeliveryFailures.fr,
    },
  },
  verificationReview: {
    en: {
      breadcrumbHome: "Home",
      breadcrumbServices: "Services",
      audience: "Leadership teams deciding whether to fund, launch, or scale an AI initiative",
      boundaryAria: "Engagement boundary",
      standardEngagement: "Standard engagement",
      faqTitle: "Questions before you apply.",
    },
    fr: {
      breadcrumbHome: "Accueil",
      breadcrumbServices: "Mandats",
      audience: "Directions qui doivent décider du financement, du lancement ou de l’expansion d’une initiative d’IA",
      boundaryAria: "Limites du mandat",
      standardEngagement: "Mandat standard",
      faqTitle: "Questions avant de présenter une demande.",
    },
  },
  verificationApplication: {
    en: {
      confirmationPrefix: "Confirmed",
      retryAfter: (minutes: number) => ` Try again in about ${minutes} minute${minutes > 1 ? "s" : ""}.`,
      investmentAcknowledgement: "Investment acknowledgement",
      safeSubmissionAcknowledgement: "Safe-submission acknowledgement",
      qualificationPath: "Qualification path",
      safeSubmission: "Safe submission",
      reviewBoundary: "Review the engagement boundary",
      privacyLink: "Review the privacy information.",
    },
    fr: {
      confirmationPrefix: "Confirmé",
      retryAfter: (minutes: number) => ` Réessayez dans environ ${minutes} minute${minutes > 1 ? "s" : ""}.`,
      investmentAcknowledgement: "Reconnaissance de l’investissement",
      safeSubmissionAcknowledgement: "Confirmation de transmission sécuritaire",
      qualificationPath: "Parcours de qualification",
      safeSubmission: "Transmission sécuritaire",
      reviewBoundary: "Revoir le périmètre du mandat",
      privacyLink: "Consulter les renseignements sur la confidentialité.",
    },
  },
  article: {
    en: {
      breadcrumbHome: "Home",
      breadcrumbInsights: "Insights",
      allInsights: "All insights",
      loading: "Loading article",
      takeaways: "Key takeaways",
      sources: "Sources and references",
      sourcesIntro: "Authoritative references used to ground the operating guidance in this article.",
      newTabLabel: (label: string) => `${label} (opens in a new tab)`,
      continue: "Continue with Vert AI",
      previous: "Previous article",
      next: "Next article",
    },
    fr: {
      breadcrumbHome: "Accueil",
      breadcrumbInsights: "Perspectives",
      allInsights: "Toutes les perspectives",
      loading: "Chargement de l’article",
      takeaways: "Points clés",
      sources: "Sources et références",
      sourcesIntro: "Références reconnues utilisées pour étayer les conseils opérationnels de cet article. Les sources externes sont en anglais, sauf indication contraire.",
      newTabLabel: (label: string) => `${label} (s’ouvre dans un nouvel onglet)`,
      continue: "Continuer avec Vert AI",
      previous: "Article précédent",
      next: "Article suivant",
    },
  },
  privacy: {
    en: {
      eyebrow: "Website information",
      breadcrumbHome: "Home",
      title: "Website privacy",
      intro: "This page covers the public Vert AI website. Client projects require separate, project-specific data, security, and retention terms.",
      updated: "Last updated: August 24, 2026",
      sections: [
        {
          title: "Information you choose to provide",
          paragraphs: [
            "The contact form asks only for your name, work email, company, and the workflow or decision you want to discuss.",
            "The AI Verification Review application asks for applicant and company identity, initiative stage, the leadership decision and deadline, high-level stakes and available evidence, an accountable owner, timing, and required investment and safe-submission acknowledgements. Company website and other context are optional.",
            "The AI readiness scorecard calculates a directional result in your browser. Your answers are sent to Vert AI only if you choose to share the result and enter an email address or use the email fallback.",
          ],
        },
        {
          title: "How website information is used",
          paragraphs: [
            "Vert AI uses inquiry and voluntarily shared scorecard information to respond, understand the operating problem, assess mutual fit, and prepare a relevant next step. Form submissions are transmitted through Vert AI’s configured form and email-delivery systems.",
            "No analytics provider is enabled by default. If Vert AI configures a privacy-approved provider, the site may record limited interaction events restricted to sanitized page paths, content slugs, CTA targets, and interface locations. Names, emails, company names, messages, answers, scores, tokens, query strings, and full referrers are rejected.",
          ],
        },
        {
          title: "Service providers and external links",
          paragraphs: [
            "Vert AI uses website hosting, Supabase-based form delivery, and email-delivery infrastructure to operate this site. Selecting a Calendly booking link takes you to Calendly, where its own privacy terms apply.",
            "A language preference may be stored in your browser so the interface can remember it. The URL remains the authoritative language signal, and you can switch languages at any time.",
          ],
        },
        {
          title: "Your choices",
          paragraphs: [
            "You can use the scorecard without sharing an email, contact Vert AI directly by email, or avoid the external booking service. To ask about information you submitted, request a correction, or request deletion where applicable, contact Vert AI at the address below.",
            "Vert AI does not use this website privacy page to make claims about the handling of data inside a future client system. Those controls are defined during scoping and documented for the specific engagement.",
          ],
        },
      ],
      contactTitle: "Privacy questions",
      contactBody: "Include enough context to identify the form submission or communication you are asking about. Do not send confidential client data through a website inquiry.",
      back: "Discuss a production AI opportunity",
    },
    fr: {
      eyebrow: "Renseignements du site",
      breadcrumbHome: "Accueil",
      title: "Confidentialité du site Web",
      intro: "Cette page porte sur le site Web public de Vert AI. Les projets clients exigent des modalités distinctes et propres au mandat pour les données, la sécurité et la conservation.",
      updated: "Dernière mise à jour : 24 août 2026",
      sections: [
        {
          title: "Renseignements que vous choisissez de fournir",
          paragraphs: [
            "Le formulaire de contact demande seulement votre nom, votre adresse courriel professionnelle, votre entreprise ainsi que le processus ou la décision dont vous souhaitez discuter.",
            "La demande d’examen de vérification de l’IA recueille l’identité de la personne et de l’entreprise, l’étape de l’initiative, la décision de direction et sa date limite, les enjeux généraux, les preuves disponibles, la personne responsable, l’échéancier ainsi que les confirmations requises sur l’investissement et la transmission sécuritaire. Le site Web de l’entreprise et le contexte supplémentaire sont facultatifs.",
            "La grille de préparation à l’IA calcule un résultat indicatif dans votre navigateur. Vos réponses sont transmises à Vert AI seulement si vous choisissez de partager le résultat et d’indiquer une adresse courriel, ou si vous utilisez l’option d’envoi par courriel.",
          ],
        },
        {
          title: "Utilisation des renseignements du site",
          paragraphs: [
            "Vert AI utilise les demandes et les résultats volontairement partagés pour vous répondre, comprendre le problème opérationnel, évaluer l’adéquation mutuelle et préparer une prochaine étape pertinente. Les formulaires sont transmis au moyen des systèmes de formulaires et d’envoi de courriels configurés par Vert AI.",
            "Aucun fournisseur d’analytique n’est activé par défaut. Si Vert AI configure un fournisseur approuvé sur le plan de la confidentialité, le site pourra consigner des interactions limitées aux chemins de page épurés, aux identifiants de contenu, aux destinations des appels à l’action et aux emplacements dans l’interface. Les noms, adresses courriel, entreprises, messages, réponses, résultats, jetons, paramètres d’URL et référents complets sont rejetés.",
          ],
        },
        {
          title: "Fournisseurs de services et liens externes",
          paragraphs: [
            "Vert AI utilise des services d’hébergement, une infrastructure Supabase pour la transmission des formulaires et des services de livraison de courriels. Un lien de réservation Calendly vous mène vers Calendly, où s’appliquent les modalités de confidentialité de cette entreprise.",
            "Une préférence linguistique peut être enregistrée dans votre navigateur. L’URL demeure le signal linguistique de référence, et vous pouvez changer de langue en tout temps.",
          ],
        },
        {
          title: "Vos choix",
          paragraphs: [
            "Vous pouvez utiliser la grille sans partager d’adresse courriel, écrire directement à Vert AI ou ne pas utiliser le service de réservation externe. Pour poser une question sur les renseignements transmis, demander une correction ou demander leur suppression lorsque cela s’applique, communiquez avec Vert AI à l’adresse ci-dessous.",
            "Cette page ne décrit pas le traitement des données dans un éventuel système client. Ces contrôles sont définis pendant le cadrage et documentés pour chaque mandat.",
          ],
        },
      ],
      contactTitle: "Questions sur la confidentialité",
      contactBody: "Fournissez assez de contexte pour identifier le formulaire ou la communication visée. Ne transmettez aucune donnée client confidentielle dans une demande Web.",
      back: "Discuter d’un projet d’IA en production",
    },
  },
  sampleReview: {
    en: {
      breadcrumbHome: "Home",
      breadcrumbResources: "Resources",
      assetLanguage: "English",
      decisionOutcome: "Decision outcome",
      outcomeSummary: "Pilot preserved. National expansion withheld.",
      showFrenchCompanion: false,
      scenarioBoundaries: [
        { label: "Reviewed boundary", value: "Ontario · English · personal property · below C$25,000 · mandatory approval" },
        { label: "Proposed boundary", value: "National · English and French · property and auto · reduced review for selected cases" },
      ],
      requestedDecision: "Requested decision · National scale",
      recommendationTerms: ["Decisive evidence", "Remaining uncertainty", "Reopen condition"],
      fictionalArtifacts: "Artifacts in the fictional scenario",
      decisionCritical: "Decision-critical",
      primary: "Primary",
      openMemo: "Open the Illustrative Decision Memo",
      openBrief: "Open the Executive Brief",
      apply: "Apply for the AI Verification Review",
    },
    fr: {
      breadcrumbHome: "Accueil",
      breadcrumbResources: "Ressources",
      assetLanguage: "anglais",
      decisionOutcome: "Résultat de la décision",
      outcomeSummary: "Projet pilote maintenu. Expansion nationale retenue.",
      showFrenchCompanion: true,
      scenarioBoundaries: [
        { label: "Périmètre examiné", value: "Ontario · anglais · biens personnels · moins de 25 000 $ CA · approbation obligatoire" },
        { label: "Périmètre proposé", value: "National · anglais et français · biens et automobile · validation réduite pour certains dossiers" },
      ],
      requestedDecision: "Décision demandée · Expansion nationale",
      recommendationTerms: ["Preuve décisive", "Incertitude restante", "Règle de réouverture"],
      fictionalArtifacts: "Documents du scénario fictif",
      decisionCritical: "Décisionnel",
      primary: "Principal",
      openMemo: "Ouvrir la note décisionnelle fictive",
      openBrief: "Ouvrir la présentation de direction",
      apply: "Présenter une demande d’examen",
    },
  },
  notFound: {
    en: { contact: "Contact Vert AI" },
    fr: { contact: "Contacter Vert AI" },
  },
  ctaSection: {
    en: { defaultEyebrow: "Next decision" },
    fr: { defaultEyebrow: "Prochaine décision" },
  },
  footer: {
    en: {
      solutions: "Solutions catalogue",
      industriesOverview: "Industries overview",
      beyondCommerce: "Beyond commerce",
      primaryCta: "Discuss a production AI opportunity",
      privacy: "Privacy",
      vetVendor: "How to vet an AI vendor",
      productionAi: "Production-grade AI",
      workflows: "Which workflows deserve AI",
      homeAria: "Vert AI home",
      navAria: "Vert AI footer navigation",
      contact: "Contact Vert AI",
      linksAria: "Footer links",
      backToTop: "Back to top",
    },
    fr: {
      solutions: "Catalogue de solutions",
      industriesOverview: "Aperçu des secteurs",
      beyondCommerce: "Au-delà du commerce",
      primaryCta: "Discuter d’un projet d’IA en production",
      privacy: "Confidentialité",
      vetVendor: "Évaluer un fournisseur d’IA",
      productionAi: "IA en production",
      workflows: "Quels processus méritent l’IA",
      homeAria: "Accueil de Vert AI",
      navAria: "Navigation du pied de page de Vert AI",
      contact: "Contacter Vert AI",
      linksAria: "Liens du pied de page",
      backToTop: "Revenir en haut de la page",
    },
  },
  navbar: {
    en: {
      detailedGuide: "Detailed sector guide",
      beyondCommerce: "Beyond commerce",
      beyondCommerceDescription: "Manufacturing, distribution, and professional services mapped to relevant production patterns.",
      directDescriptions: {
        solutions: "Proven system patterns for defined operating problems.",
        work: "Delivery proof, founder experience, and production standards.",
        insights: "Frameworks and analysis on production AI.",
        about: "How Vert AI works and who builds it.",
      },
      overview: "Overview",
      specializedDecisionReview: "Specialized decision review",
      showMenu: (label: string) => `Show ${label}`,
      specializedReview: "Specialized review",
      reviewBoundary: "The Verification Review supports a decision on an existing initiative; it does not replace an implementation engagement.",
      homeAria: "Vert AI home",
      servicesOverview: "Compare AI, data, and decision-system engagements by operating problem.",
      industriesOverview: "Compare published sector depth with additional operating contexts.",
      themeAria: "Toggle colour theme",
      switchLocaleAria: "Switch to French",
      targetLocaleLabel: "FR",
      mobileNavAria: "Vert AI primary navigation",
      mobileServicesOverview: "Choose the right engagement.",
      mobileIndustriesOverview: "Compare detailed guides and additional operating contexts.",
    },
    fr: {
      detailedGuide: "Guide sectoriel détaillé",
      beyondCommerce: "Au-delà du commerce",
      beyondCommerceDescription: "Fabrication, distribution et services professionnels reliés aux modèles de production pertinents.",
      directDescriptions: {
        solutions: "Des configurations éprouvées pour des problèmes opérationnels précis.",
        work: "Preuves de livraison, expérience du fondateur et normes de production.",
        insights: "Cadres de travail et analyses sur l’IA en production.",
        about: "La façon dont Vert AI travaille et les personnes qui réalisent les mandats.",
      },
      overview: "Vue d’ensemble",
      specializedDecisionReview: "Examen décisionnel spécialisé",
      showMenu: (label: string) => `Afficher ${label}`,
      specializedReview: "Examen spécialisé",
      reviewBoundary: "L’examen de vérification soutient une décision sur une initiative existante; il ne remplace pas un mandat de mise en œuvre.",
      homeAria: "Accueil de Vert AI",
      servicesOverview: "Comparer les mandats liés à l’IA, aux données et à la décision selon le problème à régler.",
      industriesOverview: "Comparer l’expertise sectorielle publiée et les autres contextes opérationnels.",
      themeAria: "Changer le thème de couleur",
      switchLocaleAria: "Passer au site en anglais",
      targetLocaleLabel: "EN",
      mobileNavAria: "Navigation principale de Vert AI",
      mobileServicesOverview: "Choisir le bon mandat.",
      mobileIndustriesOverview: "Comparer les guides détaillés et les autres contextes opérationnels.",
    },
  },
  proofBar: {
    en: { aria: "Proof points" },
    fr: { aria: "Points de preuve" },
  },
  routeExperience: {
    en: {
      loading: "Loading page",
      loaded: (pageName: string) => `${pageName}, page loaded.`,
      errorEyebrow: "Loading error",
      errorTitle: "This page could not be loaded.",
      errorBody: "Your information was not submitted. Retry, or continue to a stable page.",
      retry: "Retry",
      home: "Home",
      contact: "Contact",
    },
    fr: {
      loading: "Chargement de la page",
      loaded: (pageName: string) => `${pageName}, page chargée.`,
      errorEyebrow: "Erreur de chargement",
      errorTitle: "Cette page n’a pas pu être chargée.",
      errorBody: "Vos renseignements n’ont pas été envoyés. Réessayez ou poursuivez vers une page stable.",
      retry: "Réessayer",
      home: "Accueil",
      contact: "Contact",
    },
  },
  industryLanding: {
    en: {
      breadcrumbHome: "Home",
      breadcrumbIndustries: "Industries",
      flowNodes: [
        { label: "Friction", sub: "Industry" },
        { label: "Data", sub: "Foundation" },
        { label: "System", sub: "Workflow" },
        { label: "Control", sub: "Production", emphasis: true },
      ],
      flowStamp: "INDUSTRY / SYSTEM",
      stallsEyebrow: "What stalls before model choice",
      stallsTitle: "Operating constraints determine what is viable.",
      workflowsEyebrow: "Operating workflows to evaluate",
      workflowsTitle: "Concrete patterns, without assumed outcomes.",
      dataEyebrow: "Required data foundation",
      dataTitle: "What must be connected before automation.",
      startingPoint: "Practical starting point",
      controlsEyebrow: "Production controls",
      controlsTitle: "Every risk needs a rule and an owner.",
      measuresEyebrow: "Measures to establish first",
      measuresTitle: "Measure before promising.",
      relatedServices: "Related services",
      relatedServicesTitle: "The services that support this industry.",
      viewService: "View service",
      faqTitle: "Common questions for this industry.",
      nextStep: "Next step",
      defaultCtaTitle: "Start with the workflow that is already expensive.",
      defaultCtaBody: "Describe the workflow, available data, and decision you need to improve. Vert AI will tell you whether an AI system is justified or whether data work should come first.",
      fitBoundary: "Fit boundary",
      relatedIndustries: "Related industries",
    },
    fr: {
      breadcrumbHome: "Accueil",
      breadcrumbIndustries: "Secteurs",
      flowNodes: [
        { label: "Friction", sub: "Secteur" },
        { label: "Données", sub: "Fondation" },
        { label: "Système", sub: "Processus" },
        { label: "Contrôle", sub: "Production", emphasis: true },
      ],
      flowStamp: "INDUSTRIE / SYSTÈME",
      stallsEyebrow: "Ce qui bloque avant le modèle",
      stallsTitle: "Les contraintes opérationnelles déterminent ce qui est viable.",
      workflowsEyebrow: "Processus opérationnels à évaluer",
      workflowsTitle: "Des pistes concrètes, sans résultat présumé.",
      dataEyebrow: "Fondation de données",
      dataTitle: "Ce qu’il faut relier avant d’automatiser.",
      startingPoint: "Point de départ concret",
      controlsEyebrow: "Contrôles de production",
      controlsTitle: "Chaque risque doit avoir une règle et un responsable.",
      measuresEyebrow: "Mesures de référence",
      measuresTitle: "Mesurer avant de promettre.",
      relatedServices: "Mandats liés",
      relatedServicesTitle: "Les mandats qui soutiennent ce secteur.",
      viewService: "Voir le mandat",
      faqTitle: "Questions fréquentes pour ce secteur.",
      nextStep: "Prochaine étape",
      defaultCtaTitle: "Commencer par le processus qui coûte déjà cher.",
      defaultCtaBody: "Décrivez le processus, les données disponibles et la décision à améliorer. Vert AI vous dira si un système d’IA est justifié ou si un travail sur les données doit venir d’abord.",
      fitBoundary: "Conditions d’adéquation",
      relatedIndustries: "Secteurs liés",
    },
  },
  servicePage: {
    en: {
      breadcrumbHome: "Home",
      breadcrumbServices: "Services",
      timing: "Timing",
      flowStamp: "SERVICE / PRODUCTION",
      definition: "Service definition",
      definitionTitle: "What this engagement delivers.",
      investment: "Investment",
      buyingSituation: "Buying situation",
      buyingSituationTitle: "Establish fit before defining scope.",
      strongFit: "Strong fit",
      notFit: "Not a fit",
      problems: "Problems addressed",
      priorities: "Priorities",
      builds: "What Vert AI builds",
      buildsTitle: "Components that end up in the workflow.",
      useCases: "Example use cases",
      useCasesTitle: "Where this service often applies.",
      inputs: "Data and workflow inputs",
      inputsTitle: "What must be understood before the build.",
      considerations: "Production considerations",
      considerationsTitle: "The controls that protect value.",
      phases: "Engagement phases",
      phasesTitle: "From scope to an inspectable output.",
      responsibilities: "Responsibilities",
      responsibilitiesTitle: "Delivery moves only when both sides own their part.",
      vertAi: "Vert AI",
      clientTeam: "Client team",
      outputs: "Engagement outputs",
      outputsTitle: "Concrete outputs the buyer can inspect.",
      metrics: "Metrics that may improve",
      metricsTitle: "Measure change against the operating baseline.",
      metricsBoundary: "These metrics define what should be observed. Actual movement depends on context, data, adoption, and operating constraints.",
      supportProof: "Support and proof",
      supportProofTitle: "What supports operation and the launch decision.",
      supportModel: "Support model",
      evidence: "Inspectable evidence",
      faqTitle: "Common questions about this service.",
      nextDecision: "Next decision",
      relatedServices: "Related services",
      industries: "Industries",
      company: "Company",
      about: "About Vert AI",
      work: "Work and delivery standards",
      nextStep: "Next step",
      primaryCta: "Discuss a production AI opportunity",
      compareServices: "Compare all services",
    },
    fr: {
      breadcrumbHome: "Accueil",
      breadcrumbServices: "Mandats",
      timing: "Échéancier",
      flowStamp: "MANDAT / PRODUCTION",
      definition: "Définition du mandat",
      definitionTitle: "Ce que ce mandat livre.",
      investment: "Investissement",
      buyingSituation: "Situation d’achat",
      buyingSituationTitle: "Vérifier l’adéquation avant de définir le périmètre.",
      strongFit: "Bonne adéquation",
      notFit: "Ne convient pas",
      problems: "Problèmes traités",
      priorities: "Priorités",
      builds: "Ce que Vert AI construit",
      buildsTitle: "Des composants intégrés au processus opérationnel.",
      useCases: "Cas d’usage",
      useCasesTitle: "Où ce mandat s’applique souvent.",
      inputs: "Intrants requis",
      inputsTitle: "Ce qu’il faut comprendre avant de construire.",
      considerations: "Considérations de production",
      considerationsTitle: "Les contrôles qui protègent la valeur.",
      phases: "Phases du mandat",
      phasesTitle: "Du cadrage à un livrable vérifiable.",
      responsibilities: "Responsabilités",
      responsibilitiesTitle: "La livraison avance seulement si les deux parties assument leur rôle.",
      vertAi: "Vert AI",
      clientTeam: "Équipe cliente",
      outputs: "Livrables du mandat",
      outputsTitle: "Des livrables concrets que l’acheteur peut examiner.",
      metrics: "Métriques pouvant s’améliorer",
      metricsTitle: "Mesurer l’évolution par rapport à la référence.",
      metricsBoundary: "Ces mesures définissent ce qu’il faut observer. Leur évolution réelle dépend du contexte, des données, de l’adoption et des contraintes opérationnelles.",
      supportProof: "Soutien et preuves",
      supportProofTitle: "Ce qui soutient l’exploitation et la décision de lancement.",
      supportModel: "Modèle de soutien",
      evidence: "Preuves vérifiables",
      faqTitle: "Questions fréquentes sur ce mandat.",
      nextDecision: "Prochaine décision",
      relatedServices: "Mandats liés",
      industries: "Secteurs",
      company: "Entreprise",
      about: "À propos de Vert AI",
      work: "Preuves et normes de livraison",
      nextStep: "Prochaine étape",
      primaryCta: "Discuter d’un projet d’IA en production",
      compareServices: "Comparer tous les mandats",
    },
  },
  solutionPage: {
    en: {
      breadcrumbHome: "Home",
      breadcrumbSolutions: "Solutions",
      serviceType: "AI implementation",
      fallbackFlow: [
        { label: "Problem", sub: "Scope" },
        { label: "Engagement", sub: "Build" },
        { label: "System", sub: "Production" },
        { label: "Outcome", sub: "Operate" },
      ],
      allSolutions: "All solutions",
      flowStamp: "SOLUTION / FLOW",
      human: "HUMAN /",
      frame: "Frame / Engagement",
      adjacent: "Adjacent solutions",
      end: "END /",
      exploreSystems: "Explore production AI systems",
    },
    fr: {
      breadcrumbHome: "Accueil",
      breadcrumbSolutions: "Solutions",
      serviceType: "Mise en œuvre de systèmes d’IA",
      fallbackFlow: [
        { label: "Problème", sub: "Cadrage" },
        { label: "Mandat", sub: "Mise en œuvre" },
        { label: "Système", sub: "Production" },
        { label: "Résultat", sub: "Exploitation" },
      ],
      allSolutions: "Toutes les solutions",
      flowStamp: "SOLUTION / PROCESSUS",
      human: "HUMAIN /",
      frame: "Cadre / Mandat",
      adjacent: "Solutions adjacentes",
      end: "FIN /",
      exploreSystems: "Explorer les systèmes d’IA en production",
    },
  },
  skipLink: {
    en: { label: "Skip to main content" },
    fr: { label: "Passer au contenu principal" },
  },
  toaster: {
    en: { close: "Close notification" },
    fr: { close: "Fermer la notification" },
  },
} as const satisfies Record<string, Readonly<Record<Locale, unknown>>>;

export type UtilityRouteCopy = typeof utilityRouteCopy;
